import React, { useState, useEffect, useCallback } from "react";
import { clsx } from "clsx";
import { EnvelopeIcon } from "@heroicons/react/20/solid";
import {
	CarIcon,
	SailboatIcon,
	MopedFrontIcon,
	VanIcon,
	IslandIcon,
	TractorIcon,
	TruckTrailerIcon,
	SuitcaseIcon,
	CowIcon,
	HouseLineIcon,
	BarnIcon,
	DotsThreeOutlineIcon,
} from "@phosphor-icons/react";
import {
	LOAN_PURPOSE_PERSONAL,
	LOAN_TYPE_BIKE,
	LOAN_TYPE_BOAT,
	LOAN_TYPE_BUSINESS,
	LOAN_TYPE_CAR,
	LOAN_TYPE_CARAVAN,
	LOAN_TYPE_EQUIPMENT,
	LOAN_TYPE_MOTORCYCLE,
	LOAN_TYPE_PERSONAL,
	LOAN_TYPE_TRUCK,
	LOAN_PURPOSE_WORKING_CAPITAL,
	LOAN_PURPOSE_BUSINESS_VEHICLE,
	LOAN_PURPOSE_EQUIPMENT,
	LOAN_PURPOSE_TRUCK,
	LOAN_TYPE_LIVESTOCK,
	LOAN_TYPE_HOME,
	LOAN_TYPE_OTHER,
	loanFieldsMap,
	stateOption,
	LOAN_TYPE_AGRI_PROPERTY,
	emailTypos,
} from "@/lib/constants";
import { useFormDataStore, useFormStateStore } from "@/components/context/useFormContext";
import { ToggleSwitch, RadioGroupField } from "@/components/ui";
import { secureInput } from "@/lib/security";

interface Step1Props {
	loanType?: string;
}

export const PersonalDetails: React.FC<Step1Props> = ({ loanType }) => {
	// Get values and setters from Zustand store
	const { formValues, setFormValue, resetFormValuesExcept } = useFormDataStore();
	const {
		selectedIndex,
		loanAmountMin,
		setEnabledFields,
		enabledLoans,
		setValidateFormDetails,
		setFormError,
		formContainer,
		toggleTC,
		setToggleTC,
	} = useFormStateStore();

	// Toggle Switch for T&C
	const [enabledTC, setEnabledTC] = useState(toggleTC);

	useEffect(() => {
		setToggleTC(enabledTC);
		if (errors.tc) {
			setErrors((prev) => ({ ...prev, tc: !enabledTC }));
		}
	}, [enabledTC]);

	const {
		firstName,
		lastName,
		email,
		state,
		loanAmount,
		loanType: zLoanType,
		loanPurpose,
	} = formValues;

	// Local state for form validation errors
	const [errors, setErrors] = useState({
		firstName: false,
		lastName: false,
		email: false,
		loanType: false,
		state: false,
		loanAmount: false,
		tc: false,
	});

	const [emailMessage, setEmailMessage] = useState("");
	const [isNavDivSmall, setIsNavDivSmall] = useState(false);

	// For form container
	useEffect(() => {
		const limiter = 520;
		setIsNavDivSmall(formContainer < limiter);
	}, [selectedIndex, formContainer]);

	// Email Typo Validation Helper
	const getEmailValidationError = (emailValue: string): string => {
		const trimmed = emailValue.trim();
		if (trimmed === "") return "Email is required";

		const sanitizedValue = secureInput.email(trimmed);
		if (!sanitizedValue.isValid) return "Email is not correctly formatted";

		const fullValue = trimmed.toLowerCase();
		const parts = fullValue.split("@");

		if (parts.length === 2) {
			const [username, domain] = parts;
			const typoRegex = /\.(con\.au|cm\.au|con|cm|co)$/i;
			const endTypoRegex = /\.com[a-z]$/;

			if (emailTypos[domain]) {
				return `Did you mean ${username}@${emailTypos[domain]}?`;
			} else if (typoRegex.test(domain)) {
				const fixedDomain = domain.replace(typoRegex, (match) => {
					return match.includes(".au") ? ".com.au" : ".com";
				});
				return `Did you mean ${username}@${fixedDomain}?`;
			} else if (endTypoRegex.test(domain)) {
				const fixedDomain = domain.replace(endTypoRegex, (match) => {
					return match.includes(".au") ? ".com.au" : ".com";
				});
				return `Did you mean ${username}@${fixedDomain}?`;
			}
		}
		return "";
	};

	// Handle input changes
	const handleFirstNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			const sanitizedValue = secureInput.name(value);
			setFormValue("firstName", sanitizedValue.sanitized);
			setErrors((prev) => ({ ...prev, firstName: !sanitizedValue.isValid }));
		},
		[setFormValue],
	);

	const handleLastNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			const sanitizedValue = secureInput.name(value);
			setFormValue("lastName", sanitizedValue.sanitized);
			setErrors((prev) => ({ ...prev, lastName: !sanitizedValue.isValid }));
		},
		[setFormValue],
	);

	const handleEmailChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			const sanitizedValue = secureInput.email(value);
			setFormValue("email", sanitizedValue.sanitized);

			const emailErrorMsg = getEmailValidationError(sanitizedValue.sanitized);
			setErrors((prev) => ({ ...prev, email: emailErrorMsg !== "" }));
			setEmailMessage(emailErrorMsg);
		},
		[setFormValue],
	);

	const handleBlur = (field: "firstName" | "lastName" | "email", value: string) => {
		if (!value) return;

		if (field === "email") {
			setFormValue("email", value.toLowerCase());
		} else {
			// Capitalise first character, and convert ALL-CAPS strings to Capitalised
			const forcedCasing =
				value === value.toUpperCase()
					? value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
					: value.charAt(0).toUpperCase() + value.slice(1);

			setFormValue(field, forcedCasing);
		}
	};

	const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const value = e.target.value;
		setFormValue("state", value);
		setErrors((prev) => ({ ...prev, state: !value }));
	};

	const handleLoanAmountChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			let formattedValue = +e.currentTarget.value.replace(/[^0-9]/g, "");
			setFormValue("loanAmount", formattedValue.toLocaleString());
			setErrors((prev) => ({
				...prev,
				loanAmount: formattedValue < loanAmountMin,
			}));
		},
		[setFormValue, loanAmountMin],
	);

	const handleLoanTypeChange = useCallback(
		(value: string) => {
			setFormValue("loanType", value);
			setErrors((prev) => ({ ...prev, loanType: !value }));
			if (value) {
				setEnabledFields(value);
			}

			if (
				loanFieldsMap[value]?.loanPurpose[0].value === LOAN_PURPOSE_PERSONAL ||
				(loanFieldsMap[value]?.loanPurpose.length == 2 &&
					loanFieldsMap[value]?.loanPurpose[1].value === LOAN_PURPOSE_PERSONAL)
			) {
				setFormValue("abnStatus", "");
				setFormValue("gstStatus", "");
			}

			if (
				value !== LOAN_TYPE_BUSINESS &&
				[
					LOAN_PURPOSE_WORKING_CAPITAL,
					LOAN_PURPOSE_BUSINESS_VEHICLE,
					LOAN_PURPOSE_EQUIPMENT,
					LOAN_PURPOSE_TRUCK,
				].includes(loanPurpose)
			) {
				setFormValue("loanPurpose", "");
				setFormValue("abnStatus", "");
				setFormValue("gstStatus", "");
				setFormValue("assetType", "");
				setFormValue("businessAbnStatus", "");
				setFormValue("businessName", "");
				setFormValue("financeReason", "");
			}

			if (value === LOAN_TYPE_LIVESTOCK) {
				resetFormValuesExcept([
					"firstName",
					"lastName",
					"email",
					"state",
					"loanType",
					"loanAmount",
				]);
			}
		},
		[setFormValue, setEnabledFields, loanPurpose, resetFormValuesExcept],
	);

	useEffect(() => {
		const hasError = Object.values(errors).some((error) => error);
		setFormError(hasError);
	}, [errors, setFormError]);

	// Function to validate all fields
	const validateForm = () => {
		const emailErrorMsg = getEmailValidationError(email);

		const newErrors = {
			firstName: !secureInput.name(firstName).isValid,
			lastName: !secureInput.name(lastName).isValid,
			email: emailErrorMsg !== "",
			state: !state,
			loanAmount: !loanAmount || parseFloat(loanAmount.replace(/,/g, "")) < loanAmountMin,
			loanType: !loanType,
			tc: !enabledTC,
		};

		setErrors(newErrors);
		setEmailMessage(emailErrorMsg);

		const hasError = Object.values(newErrors).some((error) => error);
		setFormError(hasError);

		return !hasError;
	};

	useEffect(() => {
		setValidateFormDetails(validateForm);
	}, [firstName, lastName, email, state, loanAmount, zLoanType, loanType, enabledTC]);

	return (
		<div className="jF-space-y-2">
			<div
				className={clsx(
					isNavDivSmall ? "jF-flex-col jF-gap-x-2" : "jF-flex-row jF-gap-x-2",
					"jF-flex",
				)}
			>
				<div className="jF-flex-1">
					<div className="jF-relative jF-mt-2">
						<input
							value={firstName}
							onChange={handleFirstNameChange}
							onBlur={(e) => handleBlur("firstName", e.target.value)}
							aria-label="First Name"
							type="text"
							id="firstName"
							placeholder="First Name"
							maxLength={30}
							autoComplete="given-name"
							className={clsx(
								"jF-capitalize",
								errors.firstName
									? "jF-ring-red-500 focus:jF-ring-red-500"
									: "jF-ring-gray-300 focus:jF-ring-[--jF-theme-color]",
								"jF-block jF-w-full jF-rounded-md jF-shadow-sm jF-border-0 jF-py-2.5 jF-text-gray-900 jF-ring-1 jF-outline-1 jF-outline-gray-900 jF-ring-inset focus:jF-ring-1 focus:jF-ring-inset focus-visible:jF-outline-[--jF-theme-color] jF-pl-3 placeholder:jF-text-gray-400 sm:jF-text-md sm:jF-leading-6",
							)}
						/>
					</div>
					{errors.firstName && (
						<span className="jF-text-xs jF-text-red-500">First Name Required</span>
					)}
				</div>
				<div className="jF-flex-1">
					<div className="jF-relative jF-mt-2">
						<input
							value={lastName}
							onChange={handleLastNameChange}
							onBlur={(e) => handleBlur("lastName", e.target.value)}
							aria-label="Last Name"
							type="text"
							id="lastName"
							placeholder="Last Name"
							maxLength={30}
							autoComplete="family-name"
							className={clsx(
								"jF-capitalize",
								errors.lastName
									? "jF-ring-red-500 focus:jF-ring-red-500"
									: "jF-ring-gray-300 focus:jF-ring-[--jF-theme-color]",
								"jF-block jF-w-full jF-rounded-md jF-shadow-sm jF-border-0 jF-py-2.5 jF-text-gray-900 jF-ring-1 jF-outline-1 jF-outline-gray-900 jF-ring-inset focus:jF-ring-1 focus:jF-ring-inset focus-visible:jF-outline-[--jF-theme-color] jF-pl-3 placeholder:jF-text-gray-400 sm:jF-text-md sm:jF-leading-6",
							)}
						/>
					</div>
					{errors.lastName && (
						<span className="jF-text-xs jF-text-red-500">Last Name Required</span>
					)}
				</div>
			</div>

			<div className="jF-relative jF-mt-1">
				<div className="jF-pointer-events-none jF-absolute jF-inset-y-0 jF-left-0 jF-flex jF-items-center jF-pl-3">
					<EnvelopeIcon className="jF-h-5 jF-w-5 jF-text-gray-400" aria-hidden="true" />
				</div>
				<input
					required={true}
					value={email}
					onChange={handleEmailChange}
					onBlur={(e) => handleBlur("email", e.target.value)}
					maxLength={50}
					aria-label="Email Address"
					type="email"
					id="email"
					placeholder="Email Address"
					autoComplete="email"
					className={clsx(
						errors.email
							? "jF-ring-red-500 focus:jF-ring-red-500"
							: "jF-ring-gray-300 focus:jF-ring-[--jF-theme-color]",
						"jF-block jF-w-full jF-rounded-md jF-shadow-sm jF-border-0 jF-py-2.5 jF-pl-10 jF-text-gray-900 jF-ring-1 jF-outline-1 jF-outline-gray-900 jF-ring-inset focus:jF-ring-1 focus:jF-ring-inset focus-visible:jF-outline-[--jF-theme-color] placeholder:jF-text-gray-400 sm:jF-text-md sm:jF-leading-6 jF-bg-none",
					)}
				/>
			</div>
			{errors.email && emailMessage && (
				<span className="jF-text-xs jF-text-red-500">{emailMessage}</span>
			)}

			{/* Loan Types */}
			{enabledLoans.length > 1 && (
				<RadioGroupField
					id="loanType"
					label="Choose a Loan Type"
					value={loanType || ""}
					options={enabledLoans.map((item) => ({
						id: item.id,
						value: item.value,
						label: item.value,
						icon:
							item.value === LOAN_TYPE_CAR ? (
								<CarIcon size={24} weight="duotone" />
							) : item.value === LOAN_TYPE_BOAT ? (
								<SailboatIcon size={24} weight="duotone" />
							) : item.value === LOAN_TYPE_MOTORCYCLE ||
							  item.value === LOAN_TYPE_BIKE ? (
								<MopedFrontIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_CARAVAN ? (
								<VanIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_PERSONAL ? (
								<IslandIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_EQUIPMENT ? (
								<TractorIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_TRUCK ? (
								<TruckTrailerIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_BUSINESS ? (
								<SuitcaseIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_LIVESTOCK ? (
								<CowIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_AGRI_PROPERTY ? (
								<BarnIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_HOME ? (
								<HouseLineIcon size={24} weight="duotone" />
							) : item.value == LOAN_TYPE_OTHER ? (
								<DotsThreeOutlineIcon size={24} weight="duotone" />
							) : null,
					}))}
					onChange={(value) => {
						handleLoanTypeChange(value);
					}}
					required={true}
					error={errors.loanType ? "This field is required" : ""}
					disabled={false}
					className={""}
					gridCols={3}
					showIcons={true}
					disabledOptions={[]}
					navRef={isNavDivSmall}
				/>
			)}

			<div
				className={clsx(
					isNavDivSmall
						? "jF-flex-col jF-gap-x-2  jF-space-y-2"
						: "jF-flex-row jF-gap-x-2",
					"jF-flex",
				)}
			>
				<div className="jF-flex-1">
					<div className="jF-relative">
						<select
							value={state}
							onChange={handleStateChange}
							id="state"
							aria-label="State"
							className={clsx(
								errors.state
									? "jF-ring-red-500 focus:jF-ring-red-500"
									: "jF-ring-gray-300 focus:jF-ring-[--jF-theme-color]",
								"jF-appearance-none jF-bg-white jF-block jF-w-full jF-rounded-md jF-border-0 jF-py-2.5 jF-pl-3 jF-pr-10 jF-text-gray-900 jF-ring-1 jF-ring-inset jF-ring-gray-300 focus:jF-ring-1 focus-visible:jF-outline-[--jF-theme-color] jF-outline-1 jF-outline-gray-900 focus:jF-ring-inset sm:jF-text-md sm:jF-leading-6",
							)}
						>
							{stateOption.map((stateOptions) => (
								<option key={stateOptions.value} value={stateOptions.value}>
									{stateOptions.option}
								</option>
							))}
						</select>
						{errors.state && (
							<span className="jF-text-xs jF-text-red-500">State is required</span>
						)}
					</div>
				</div>
				<div className="jF-flex-1">
					<div className="jF-relative">
						<div className="jF-pointer-events-none jF-absolute jF-inset-y-0 jF-left-0 jF-flex jF-items-center jF-pl-3">
							<span className="jF-text-gray-500 sm:jF-text-md">$</span>
						</div>
						<input
							value={loanAmount}
							onChange={handleLoanAmountChange}
							aria-label="Loan Amount"
							placeholder={
								loanAmountMin >= 10000
									? `Loan Amount (Min 10K)`
									: loanAmountMin == 5000
										? `Loan Amount (Min 5K)`
										: loanAmountMin == 2000
											? `Loan Amount (Min 2K)`
											: ``
							}
							maxLength={10}
							className={clsx(
								errors.loanAmount
									? "jF-ring-red-500 focus:jF-ring-red-500"
									: "jF-ring-gray-300 focus:jF-ring-[--jF-theme-color]",
								"jF-block jF-w-full jF-rounded-md jF-shadow-sm jF-border-0 jF-py-2.5 jF-pl-7 jF-pr-12 jF-text-gray-900 jF-ring-1 jF-ring-inset jF-ring-gray-300 focus:jF-ring-1 jF-outline-1 focus:jF-ring-inset focus-visible:jF-outline-[--jF-theme-color] placeholder:jF-text-gray-400 sm:jF-text-md sm:jF-leading-6",
							)}
							type="text"
							id="loan-amount"
							aria-describedby="loan-amount"
							inputMode="numeric"
						/>
						<div className="jF-pointer-events-none jF-absolute jF-inset-y-0 jF-right-0 jF-flex jF-items-center jF-pr-3">
							<span className="jF-text-gray-500 sm:jF-text-md">AUD</span>
						</div>
					</div>
					{errors.loanAmount && (
						<span className="jF-text-xs jF-text-red-500">
							Minimum loan amount is ${loanAmountMin.toLocaleString()}
						</span>
					)}
				</div>
			</div>

			<div id="tc-toggle" className={clsx("!jF-mt-4")} tabIndex={-1}>
				<ToggleSwitch label="" enabled={enabledTC} setEnabled={setEnabledTC} />
				{errors.tc && (
					<span className="jF-block jF-mt-1 jF-text-xs jF-text-red-500">
						Please agree to the terms and conditions to continue
					</span>
				)}
			</div>
		</div>
	);
};
